import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, Copy, Wand2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

export const ClientPasswordCard = ({ clientId, authHeaders }) => {
  const [info, setInfo] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/customers/verified/${clientId}/password`,
        { headers: authHeaders() });
      setInfo(r.data);
      setShow(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao buscar a senha'); }
    setLoading(false);
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Senha copiada.');
    } catch { toast.error('Não foi possível copiar.'); }
  };

  const save = async (generate = false) => {
    if (!generate && newPass.trim().length < 8) {
      toast.error('A senha precisa ter pelo menos 8 caracteres.'); return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/customers/verified/${clientId}/password`,
        generate ? { generate: true } : { new_password: newPass.trim() },
        { headers: authHeaders() });
      setInfo({ visible: true, password: r.data.password,
        changed_at: r.data.changed_at, changed_by: 'admin' });
      setShow(true);
      setChanging(false);
      setNewPass('');
      toast.success('Senha alterada. Copie e envie ao cliente.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao alterar a senha'); }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-3" data-testid="client-password-card">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase text-amber-800">
        <KeyRound className="h-3.5 w-3.5" /> Senha do cliente
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Input readOnly data-testid="client-password-value"
          value={!info ? '••••••••'
            : info.visible ? (show ? info.password : '••••••••')
              : 'não visível — redefina para ver'}
          className="h-9 max-w-[240px] font-mono text-sm" />
        {!info ? (
          <Button size="sm" variant="outline" disabled={loading} onClick={load}
            data-testid="client-password-show">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Eye className="mr-1 h-3.5 w-3.5" /> Mostrar</>}
          </Button>
        ) : info.visible && (
          <>
            <Button size="sm" variant="outline" onClick={() => setShow((v) => !v)}
              data-testid="client-password-toggle">
              {show ? <EyeOff className="mr-1 h-3.5 w-3.5" /> : <Eye className="mr-1 h-3.5 w-3.5" />}
              {show ? 'Ocultar' : 'Mostrar'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => copy(info.password)}
              data-testid="client-password-copy">
              <Copy className="mr-1 h-3.5 w-3.5" /> Copiar
            </Button>
          </>
        )}
        <Button size="sm" onClick={() => setChanging((v) => !v)}
          data-testid="client-password-change-btn"
          className="bg-amber-600 text-white hover:bg-amber-700">
          <KeyRound className="mr-1 h-3.5 w-3.5" /> Alterar senha
        </Button>
      </div>

      {info?.changed_at && (
        <p className="mt-1 text-[10px] text-amber-900" data-testid="client-password-changed-at">
          Última alteração: {fmtDate(info.changed_at)}
          {info.changed_by?.startsWith('admin') ? ' · pelo admin'
            : info.changed_by === 'cliente:painel' ? ' · pelo próprio cliente'
              : info.changed_by === 'cliente:esqueci' ? ' · por "esqueci minha senha"'
                : info.changed_by === 'cliente:cadastro' ? ' · definida no cadastro' : ''}
        </p>
      )}

      {changing && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="client-password-change-box">
          <Input value={newPass} onChange={(e) => setNewPass(e.target.value)}
            placeholder="Nova senha (mín. 8 caracteres)" data-testid="client-password-new"
            className="h-9 max-w-[240px] text-sm" />
          <Button size="sm" disabled={saving} onClick={() => save(false)}
            data-testid="client-password-save"
            className="bg-amber-600 text-white hover:bg-amber-700">
            Confirmar
          </Button>
          <Button size="sm" variant="outline" disabled={saving} onClick={() => save(true)}
            data-testid="client-password-generate">
            <Wand2 className="mr-1 h-3.5 w-3.5" /> Gerar senha forte
          </Button>
        </div>
      )}

      <p className="mt-1 text-[10px] leading-relaxed text-amber-900/80">
        A senha antiga para de funcionar na hora. Contas que nunca trocaram a senha depois desta
        atualização continuam como "não visível" — nesse caso, defina uma nova e envie ao cliente.
      </p>
    </div>
  );
};

export default ClientPasswordCard;
